import { useCallback, useMemo, useRef } from 'react';
import I18n from 'I18n';
import UIInlineLabel from 'UIComponents/form/UIInlineLabel';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { ALL_BUSINESS_UNITS_ID, BU_PROPERTY_NAME } from 'framework-listing-lib/constants/businessUnits';
import { EQ, HAS_PROPERTY, IN } from 'framework-listing-lib/utils/filters';
import ConnectedBUSelect from 'framework-listing-lib/internal/ConnectedBUSelect';
import ListingLibFilterWrapper from 'framework-listing-lib/filters/ListingLibFilterWrapper';
import useFetchBusinessUnits from 'framework-listing-lib/internal/businessUnits/hooks/useFetchBusinessUnits';
import { getShouldClearOptions, getShouldSelectAllBusinessUnits } from 'framework-listing-lib/utils/businessUnitUtils';
import { useListingLibQuickFilters } from 'framework-listing-lib/hooks/useQuickFilters';
import { useHasBUNavIntegration } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import BUQuickFilterTooltip from 'framework-listing-lib/internal/businessUnits/components/BUQuickFilterTooltip';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ListingLibBUSelect({
  disabled,
  multi,
  use = 'label',
  includeViewAll = true
}) {
  const {
    getQuickFilterValue,
    quickFilters,
    removeQuickFilter,
    setQuickFilter
  } = useListingLibQuickFilters();
  const {
    data: allBUs,
    error: errorLoadingBUS,
    loading: loadingBUS
  } = useFetchBusinessUnits();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const hasBUNavIntegration = useHasBUNavIntegration();
  const isDisabled = hasBUNavIntegration || disabled;

  /**
   * Preserve @multi value when set (true / false)
   * Otherwise:
   *   - make multi true when @use is button: https://git.hubteam.com/HubSpot/CampaignsUXTaskForce/issues/7
   */
  const isMulti = useMemo(() => {
    if (multi !== undefined) {
      return multi;
    }
    return use === 'button';
  }, [multi, use]);

  /**
   * Truthy when user selects all options in the dropdown.
   */
  const selectAllRef = useRef(false);
  const value = useMemo(() => {
    const selectedBUIds = getQuickFilterValue(BU_PROPERTY_NAME);
    const isAllBUsOption = quickFilters.some(filter => filter.operator === 'HAS_PROPERTY' && filter.property === BU_PROPERTY_NAME);
    if (isMulti && isAllBUsOption) {
      return [ALL_BUSINESS_UNITS_ID, ...allBUs.map(bu => bu.id)];
    }
    if (selectedBUIds == null) {
      return undefined;
    }
    if (Array.isArray(selectedBUIds)) {
      return selectedBUIds;
    }
    return parseInt(selectedBUIds, 10);
  }, [allBUs, getQuickFilterValue, isMulti, quickFilters]);
  const handleChange = useCallback(buIdOrIds => {
    trackCrmTableInteraction({
      action: 'filter-objects-by-business-unit'
    });

    // Handle change event for "single" mode
    if (!isMulti && !Array.isArray(buIdOrIds)) {
      if (buIdOrIds != null && buIdOrIds >= 0) {
        setQuickFilter(EQ(BU_PROPERTY_NAME, buIdOrIds));
      } else {
        removeQuickFilter(BU_PROPERTY_NAME);
      }
      return;
    }

    // Handle change event for "multi" mode
    if (isMulti && (buIdOrIds === undefined || Array.isArray(buIdOrIds))) {
      const args = {
        allBusinessUnits: allBUs,
        newValue: buIdOrIds,
        hasPreviouslySelectedAll: selectAllRef.current
      };
      const shouldClear = getShouldClearOptions(args);
      if (shouldClear) {
        selectAllRef.current = false;
        removeQuickFilter(BU_PROPERTY_NAME);
        return;
      }
      const isAllBUs = getShouldSelectAllBusinessUnits(args);
      selectAllRef.current = isAllBUs;
      if (isAllBUs) {
        setQuickFilter(HAS_PROPERTY(BU_PROPERTY_NAME));
      } else if (!buIdOrIds || buIdOrIds && buIdOrIds.length === 0) {
        removeQuickFilter(BU_PROPERTY_NAME);
      } else {
        setQuickFilter(IN(BU_PROPERTY_NAME, buIdOrIds.filter(buId => buId !== ALL_BUSINESS_UNITS_ID)));
      }
    }
  }, [allBUs, isMulti, removeQuickFilter, setQuickFilter, trackCrmTableInteraction]);
  const handleClearBUs = useCallback(() => {
    removeQuickFilter(BU_PROPERTY_NAME);
  }, [removeQuickFilter]);

  /** If the portal has only a single business unit, the filter dropdown
   * won't show up, making the component useless. Therefore, there's no point rendering it
   */
  if (errorLoadingBUS || loadingBUS || allBUs.length < 2) {
    return null;
  }
  if (use === 'label') {
    return /*#__PURE__*/_jsx(BUQuickFilterTooltip, {
      children: /*#__PURE__*/_jsx(UIInlineLabel, {
        label: I18n.text('frameworkListingLib.buSelect.label'),
        children: /*#__PURE__*/_jsx(ConnectedBUSelect, {
          disabled: isDisabled,
          multi: isMulti,
          onChange: handleChange,
          options: allBUs,
          use: use,
          value: value,
          includeViewAll: includeViewAll
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(BUQuickFilterTooltip, {
    children: /*#__PURE__*/_jsx(ListingLibFilterWrapper, {
      active: value !== undefined && !isDisabled,
      onClearFilter: handleClearBUs,
      children: /*#__PURE__*/_jsx(ConnectedBUSelect, {
        disabled: isDisabled,
        multi: isMulti,
        onChange: handleChange,
        options: allBUs,
        use: use,
        value: value,
        includeViewAll: includeViewAll
      })
    })
  });
}